import { Rule } from 'antd/es/form';

/**
 * Готовые правила валидации для переиспользования в формах
 *
 * Использование:
 * import { VALIDATION_RULES } from 'shared/constants/validationRules';
 *
 * rules: [VALIDATION_RULES.email, VALIDATION_RULES.required('Email')]
 */

export const VALIDATION_RULES = {
  // Email валидация
  email: {
    type: 'email',
    message: 'Введите корректный email адрес',
  } as Rule,

  // URL валидация
  url: {
    type: 'url',
    message: 'Введите корректный URL',
  } as Rule,

  // Телефон (российский формат)
  phone: {
    pattern: /^(\+7|8)?[\s-]?\(?[489][0-9]{2}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/,
    message: 'Введите корректный номер телефона',
  } as Rule,

  // ИНН (10 или 12 цифр)
  inn: {
    pattern: /^\d{10}$|^\d{12}$/,
    message: 'ИНН должен содержать 10 или 12 цифр',
  } as Rule,

  // ОГРН (13 цифр)
  ogrn: {
    pattern: /^\d{13}$/,
    message: 'ОГРН должен содержать 13 цифр',
  } as Rule,

  // ОГРНИП (15 цифр)
  ogrnip: {
    pattern: /^\d{15}$/,
    message: 'ОГРНИП должен содержать 15 цифр',
  } as Rule,

  // КПП (9 цифр)
  kpp: {
    pattern: /^\d{9}$/,
    message: 'КПП должен содержать 9 цифр',
  } as Rule,

  // БИК (9 цифр)
  bik: {
    pattern: /^\d{9}$/,
    message: 'БИК должен содержать 9 цифр',
  } as Rule,

  // Расчетный счет (20 цифр)
  bankAccount: {
    pattern: /^\d{20}$/,
    message: 'Расчетный счет должен содержать 20 цифр',
  } as Rule,

  // Пароль (минимум 8 символов, должен содержать буквы и цифры)
  password: {
    pattern: /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/,
    message: 'Пароль должен содержать минимум 8 символов, включая буквы и цифры',
  } as Rule,

  // Сильный пароль (минимум 8 символов, строчные, заглавные буквы и цифры)
  strongPassword: {
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
    message: 'Пароль должен содержать минимум 8 символов, включая строчные, заглавные буквы и цифры',
  } as Rule,

  // Только латинские буквы и цифры
  alphanumeric: {
    pattern: /^[a-zA-Z0-9]+$/,
    message: 'Допускаются только латинские буквы и цифры',
  } as Rule,

  // Только латинские буквы, цифры и подчеркивание
  username: {
    pattern: /^[a-zA-Z0-9_]+$/,
    message: 'Допускаются только латинские буквы, цифры и подчеркивание',
  } as Rule,

  // Только цифры
  numeric: {
    pattern: /^\d+$/,
    message: 'Допускаются только цифры',
  } as Rule,

  // Запрет пробелов
  noWhitespace: {
    whitespace: true,
    message: 'Поле не может состоять только из пробелов',
  } as Rule,
};

/**
 * Фабрики правил - функции для создания правил с параметрами
 */

export const createMinLengthRule = (min: number, fieldName?: string): Rule => ({
  min,
  message: fieldName
    ? `${fieldName} должно содержать минимум ${min} символов`
    : `Минимальная длина ${min} символов`,
});

export const createMaxLengthRule = (max: number, fieldName?: string): Rule => ({
  max,
  message: fieldName
    ? `${fieldName} не должно превышать ${max} символов`
    : `Максимальная длина ${max} символов`,
});

export const createRangeRule = (min: number, max: number, fieldName?: string): Rule => ({
  validator: (_, value) => {
    const length = value ? String(value).length : 0;
    if (length < min || length > max) {
      return Promise.reject(
        new Error(
          fieldName
            ? `${fieldName} должно содержать от ${min} до ${max} символов`
            : `Длина должна быть от ${min} до ${max} символов`,
        ),
      );
    }
    return Promise.resolve();
  },
});

export const createNumericRangeRule = (min: number, max: number, fieldName?: string): Rule => ({
  type: 'number',
  min,
  max,
  message: fieldName
    ? `${fieldName} должно быть от ${min} до ${max}`
    : `Значение должно быть от ${min} до ${max}`,
});

export const createPatternRule = (pattern: RegExp, message: string): Rule => ({
  pattern,
  message,
});

export const createRequiredRule = (fieldName: string): Rule => ({
  required: true,
  message: `Поле "${fieldName}" обязательно для заполнения`,
});

/**
 * Комплексные наборы правил для типичных сценариев
 */

export const FIELD_RULES = {
  // Правила для ФИО
  fullName: [
    createMinLengthRule(2, 'Поле'),
    createMaxLengthRule(50, 'Поле'),
    VALIDATION_RULES.noWhitespace,
  ] as Rule[],

  // Правила для названия компании
  companyName: [
    createMinLengthRule(3, 'Название компании'),
    createMaxLengthRule(100, 'Название компании'),
    VALIDATION_RULES.noWhitespace,
  ] as Rule[],

  // Правила для email
  emailField: [VALIDATION_RULES.email] as Rule[],

  // Правила для телефона
  phoneField: [VALIDATION_RULES.phone] as Rule[],

  // Правила для ИНН
  innField: [VALIDATION_RULES.inn] as Rule[],

  // Правила для пароля
  passwordField: [createMinLengthRule(8, 'Пароль'), VALIDATION_RULES.strongPassword] as Rule[],

  // Правила для никнейма/логина
  usernameField: [
    createMinLengthRule(3, 'Имя пользователя'),
    createMaxLengthRule(20, 'Имя пользователя'),
    VALIDATION_RULES.username,
  ] as Rule[],

  // Правила для описания/комментария
  descriptionField: [createMaxLengthRule(500, 'Описание')] as Rule[],

  // Правила для короткого текста
  shortTextField: [createMaxLengthRule(255)] as Rule[],
};
